import { Select, Skeleton } from "antd";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { formatDate } from "../../services/util";
import CustomTable from "../common/Table";

const { Option } = Select;

const columns = [
    {
        title: "Hotel ID",
        dataIndex: "hotel_id",
        key: "hotel_id",
        width: 100,
        render: (id) => (
            <span
                style={{
                    display: "inline-block",
                    background: "var(--color-bg-elevated)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontFamily: "monospace",
                    fontSize: 12,
                    color: "var(--color-primary-hover)",
                    fontWeight: 600,
                }}
            >
                #{id}
            </span>
        ),
    },
    {
        title: "Hotel Name",
        dataIndex: "hotel_name",
        key: "hotel_name",
        render: (name) => (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                    style={{
                        width: 30,
                        height: 30,
                        borderRadius: 8,
                        background: "var(--color-primary-light)",
                        border: "1.5px solid var(--color-primary)",
                        color: "var(--color-primary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 12,
                        flexShrink: 0,
                    }}
                >
                    {name ? name[0].toUpperCase() : "H"}
                </div>
                <span
                    style={{
                        color: "var(--color-text)",
                        fontSize: 13,
                        fontWeight: 500,
                    }}
                >
                    {name || "—"}
                </span>
            </div>
        ),
    },
    {
        title: "Errors",
        dataIndex: "errors",
        key: "errors",
        render: (val) => {
            const num = Number(val);
            const color =
                num > 100
                    ? "var(--color-error)"
                    : num > 50
                    ? "var(--color-warning)"
                    : num > 0
                    ? "var(--color-warning)"
                    : "var(--color-success)";
            const bg =
                num > 100
                    ? "var(--color-error-light)"
                    : num > 50
                    ? "var(--color-warning-light)"
                    : num > 0
                    ? "var(--color-warning-light)"
                    : "var(--color-success-light)";
            const border =
                num > 100
                    ? "var(--color-error)"
                    : num > 50
                    ? "var(--color-warning)"
                    : num > 0
                    ? "var(--color-warning)"
                    : "var(--color-success)";
            return (
                <span
                    style={{
                        display: "inline-block",
                        background: bg,
                        border: `1px solid ${border}`,
                        color,
                        borderRadius: 6,
                        padding: "2px 12px",
                        fontWeight: 700,
                        fontSize: 13,
                        minWidth: 48,
                        textAlign: "center",
                    }}
                >
                    {val ?? "—"}
                </span>
            );
        },
    },
];

function ErrorAnalytics() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const { selectedDates } = useContext(SettingContext);
    const [start_date, end_date] = selectedDates;
    const [hotelOptions, setHotelOptions] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [formData, setFormData] = useState({
        staff_id,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        all_selected: true,
        hotel_ids: [],
    });

    const {
        status: hotelStatus,
        data: hotelData,
        refetch: getHotelSelectionOptions,
    } = useQuery(
        "analytics-hotels",
        async () =>
            await HTTP("/dashboard/getHotels", { staff_id, hotel_type: "all" }),
        { enabled: false },
    );
    const {
        status: tableStatus,
        data: tableRes,
        refetch: getTableData,
    } = useQuery(
        "error-analytics",
        async () =>
            await HTTP("/dashboard/getSplunkErrorCounts", { ...formData }),
        { enabled: false },
    );

    useEffect(() => {
        if (tableStatus === "success") setTableData(tableRes.data);
    }, [tableStatus, tableRes]);
    useEffect(() => {
        if (hotelStatus === "success") setHotelOptions(hotelData.data);
    }, [hotelStatus, hotelData]);
    useEffect(() => {
        getTableData();
    }, [formData]);
    useEffect(() => {
        getTableData();
        getHotelSelectionOptions();
    }, []);

    useEffect(() => {
        const [s, e] = selectedDates;
        setFormData((p) => ({
            ...p,
            start_date: formatDate(s),
            end_date: formatDate(e),
        }));
    }, [selectedDates]);

    function handleHotelSelection(e) {
        setFormData((p) => ({
            ...p,
            hotel_ids: e,
            all_selected: e.length === 0,
        }));
    }

    const isReady = tableStatus !== "loading" && tableStatus !== "idle";

    // Summary stats
    const totalErrors = tableData.reduce(
        (sum, r) => sum + (Number(r.errors) || 0),
        0,
    );
    const hotelsWithErrors = tableData.filter(
        (r) => Number(r.errors) > 0,
    ).length;

    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "20px 24px",
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 20,
                }}
            >
                <div
                    style={{
                        width: 3,
                        height: 18,
                        borderRadius: 2,
                        background: "var(--gradient-primary)",
                    }}
                />
                <span
                    style={{
                        color: "var(--color-text)",
                        fontWeight: 600,
                        fontSize: 15,
                    }}
                >
                    Error Analytics
                </span>
            </div>

            {/* Filter bar */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    background: "var(--color-bg-control)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    padding: "10px 16px",
                    marginBottom: 20,
                }}
            >
                <span
                    style={{
                        color: "var(--color-text-muted)",
                        fontSize: 12,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                    }}
                >
                    Hotels
                </span>
                <Select
                    placeholder="All hotels"
                    showSearch
                    mode="multiple"
                    loading={!hotelOptions.length}
                    style={{ flex: 1 }}
                    onChange={handleHotelSelection}
                    filterOption={(input, option) =>
                        option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                >
                    {hotelOptions.map((h) => (
                        <Option
                            key={h.hotel_id || h.id}
                            value={h.hotel_id || h.id}
                        >
                            {`${h.hotel_id || h.id} - ${
                                h.hotel_name || h.name
                            } - ${h.city}`}
                        </Option>
                    ))}
                </Select>
            </div>

            {/* Summary pills — shown once data is loaded */}
            {isReady && tableData.length > 0 && (
                <div
                    style={{
                        display: "flex",
                        gap: 12,
                        marginBottom: 20,
                        flexWrap: "wrap",
                    }}
                >
                    {[
                        {
                            label: "Total Errors",
                            value: totalErrors,
                            color: "var(--color-error)",
                        },
                        {
                            label: "Hotels with Errors",
                            value: hotelsWithErrors,
                            color: "var(--color-warning)",
                        },
                        {
                            label: "Hotels Monitored",
                            value: tableData.length,
                            color: "var(--color-primary-hover)",
                        },
                    ].map(({ label, value, color }) => (
                        <div
                            key={label}
                            style={{
                                background: "var(--color-bg-control)",
                                border: `1px solid ${color}33`,
                                borderRadius: 10,
                                padding: "10px 20px",
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            <span
                                style={{ fontSize: 20, fontWeight: 700, color }}
                            >
                                {value}
                            </span>
                            <span
                                style={{
                                    fontSize: 11,
                                    color: "var(--color-text-muted)",
                                }}
                            >
                                {label}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* Table */}
            {isReady ? (
                <CustomTable
                    columns={columns}
                    tableData={tableData}
                    rowKey="hotel_id"
                />
            ) : (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 10, width: "100%" }}
                />
            )}
        </div>
    );
}

export { ErrorAnalytics };
